import React from 'react';
import './MatchCard.css';
import { FaFutbol, FaTrophy, FaBolt, FaChartBar, FaTv } from 'react-icons/fa';
import { formatMatchDate } from '../utils/formatMatchDate';

/** Format UTC date string → local time */
function formatMatchDateLocal(value) {
  if (!value) return '';
  return formatMatchDate(value);
}

const SELECTION_LABEL = { Home: '1', Draw: 'X', Away: '2' };
const ORDER           = ['Home', 'Draw', 'Away'];

/**
 * Normalise any match shape into the API shape so the rest of the
 * component only has to deal with one format.
 *
 * API shape:   { home_team, away_team, match_date, status, odds:[{id,selection,odd_value}], … }
 * Legacy shape:{ homeTeam, awayTeam, date, score, minute, markets:{home,draw,away}, … }
 */
function normalise(m) {
  if (m.home_team) return m; // already API shape

  const mkOdd = (sel, val) =>
    val != null ? { id: `${m.id}-${sel}`, selection: sel, odd_value: val } : null;

  let status = m.status ?? 'NS';
  if (!m.status && m.score) status = 'LIVE';

  return {
    id:          m.id,
    league:      m.league     ?? '',
    league_logo: m.leagueLogo ?? null,
    home_team:   m.homeTeam   ?? '',
    home_logo:   m.homeLogo   ?? null,
    away_team:   m.awayTeam   ?? '',
    away_logo:   m.awayLogo   ?? null,
    match_date:  m.match_date ?? m.date ?? null,
    status,
    home_score:  m.homeScore  ?? (m.score ? m.score.split(/[\s-]+/)[0] : null),
    away_score:  m.awayScore  ?? (m.score ? m.score.split(/[\s-]+/).slice(-1)[0] : null),
    odds: [
      mkOdd('Home', m.markets?.home),
      mkOdd('Draw', m.markets?.draw),
      mkOdd('Away', m.markets?.away),
    ].filter(Boolean),
  };
}

/** Team logo with emoji fallback */
function TeamImg({ src, alt, size = 34 }) {
  const [broken, setBroken] = React.useState(false);
  if (!src || broken) {
    return (
      <div
        className="team-logo-fallback"
        style={{ width: size, height: size, fontSize: size * 0.55 }}
        title={alt}
      >
        <FaFutbol />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      className="team-logo-img"
      onError={() => setBroken(true)}
    />
  );
}

/** League logo with fallback */
function LeagueImg({ src, alt }) {
  const [broken, setBroken] = React.useState(false);
  if (!src || broken) return <span className="league-logo-fallback"><FaTrophy /></span>;
  return (
    <img
      src={src}
      alt={alt}
      width={16}
      height={16}
      className="league-logo-img-sm"
      onError={() => setBroken(true)}
    />
  );
}

export default function MatchCard({ match: rawMatch, addBet, bets }) {
  const match      = normalise(rawMatch);
  const isFinished = match.status === 'FT';
  const isLive     = ['LIVE', '1H', '2H', 'HT'].includes(match.status);

  const isSelected = (oddId) => bets?.some((b) => b.odd_id === oddId);

  const handleBet = (odd) => {
    const label = SELECTION_LABEL[odd.selection] ?? odd.selection;
    addBet({
      id:         `${match.id}-${odd.id}`,
      fixture_id: match.id,
      odd_id:     odd.id,
      match:      `${match.home_team} vs ${match.away_team}`,
      selection:  `${label} — ${
        odd.selection === 'Home' ? match.home_team
        : odd.selection === 'Away' ? match.away_team
        : 'Nul'
      }`,
      market: '1X2',
      odds:   odd.odd_value,
    });
  };

  const odds = [...(match.odds ?? [])].sort(
    (a, b) => ORDER.indexOf(a.selection) - ORDER.indexOf(b.selection)
  );

  return (
    <div className={`match-card ${isLive ? 'live' : ''} ${isFinished ? 'finished' : ''}`}>

      {/* Header */}
      <div className="match-header">
        <div className="match-league">
          <LeagueImg src={match.league_logo} alt={match.league} />
          <span>{match.league}</span>
        </div>
        <div className="match-meta">
          {isLive && (
            <span className="live-badge">
              <span className="live-dot-icon" />
              LIVE
            </span>
          )}
          {isFinished && <span className="ft-badge">FT</span>}
          {!isLive && !isFinished && (
            <span className="match-date">{formatMatchDateLocal(match.match_date)}</span>
          )}
        </div>
      </div>

      {/* Teams */}
      <div className="match-teams">
        <div className="team">
          <TeamImg src={match.home_logo} alt={match.home_team} size={34} />
          <span className="team-name">{match.home_team}</span>
        </div>

        <div className="match-vs">
          {isFinished || isLive ? (
            <div className={`match-score ${isLive ? 'live-score' : ''}`}>
              <span>{match.home_score ?? 0}</span>
              <span className="score-sep">—</span>
              <span>{match.away_score ?? 0}</span>
            </div>
          ) : (
            <span className="vs-text">VS</span>
          )}
        </div>

        <div className="team right">
          <span className="team-name right">{match.away_team}</span>
          <TeamImg src={match.away_logo} alt={match.away_team} size={34} />
        </div>
      </div>

      {/* Odds */}
      {!isFinished && odds.length > 0 && (
        <div className="match-odds">
          {odds.map((odd) => (
            <button
              key={odd.id}
              className={`odds-btn ${isSelected(odd.id) ? 'selected' : ''}`}
              onClick={() => handleBet(odd)}
            >
              <span className="odds-label">{SELECTION_LABEL[odd.selection] ?? odd.selection}</span>
              <span className="odds-value">{Number(odd.odd_value).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}

      {/* Finished */}
      {isFinished && (
        <div className="match-result-row">
          <span className="match-result-label">Résultat final</span>
          <span className="match-result-score">{match.home_score} — {match.away_score}</span>
        </div>
      )}

      {/* Footer */}
      <div className="match-footer">
        <button className="more-markets"><FaBolt /> +12 marchés</button>
        <div className="match-stats">
          <span><FaChartBar /> Stats</span>
          <span><FaTv /> Stream</span>
        </div>
      </div>
    </div>
  );
}
